import React, { useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useBrandLanguage } from '@context/brand-language/context';
import { guideEntryState } from '@features/knowledge-base/utils/guideNavigation';
import { HelpOutlined as HelpIcon } from '@mui/icons-material';
import { Box, Menu, MenuItem } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useUIState } from '../../context/uiState';
import { useModal } from '../../store/hooks';
import CustomButton from '../../styles/CustomButtons';
import { trackEvent } from '../../utils/analytics_utils';
import { MODAL_TYPES } from '../modals';
import { useGlobalTutorial } from '../Tutorial/GlobalTutorialContext';

type NavHelpMenuProps = {
  /** In-app guide route for "Help Articles"; when absent the item links to the external Front help center. */
  guidePath?: string;
  /** Opens in-app support; public navigation retains the external contact page. */
  onContactSupport?: () => void;
};

export const NavHelpMenu = ({ guidePath, onContactSupport }: NavHelpMenuProps) => {
  const { open } = useModal();
  const navigate = useNavigate();
  const [anchorElement, setAnchorElement] = useState<(EventTarget & HTMLButtonElement) | null>(
    null,
  );
  const pendingSupportOpen = useRef<(() => void) | null>(null);
  const { brandLanguage } = useBrandLanguage();
  const { state: UIState } = useUIState();
  const { platform } = UIState;
  const { hasTutorials, startTutorial } = useGlobalTutorial();
  const location = useLocation();
  const { navigation: navigationMetrics } = useEventTracking();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    pendingSupportOpen.current = null;
    setAnchorElement(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElement(null);
  };

  const handleHelpArticlesClick = () => {
    // Track help menu navigation
    navigationMetrics.clicked({
      menu_item: 'help_knowledgebase',
      source_path: location.pathname,
      destination_path: guidePath ?? 'external',
      is_dropdown: true,
    });
    // Keep legacy tracking for now
    trackEvent({
      event: 'help_knowledgebase_clicked',
      platform: platform,
    });
    handleClose();
  };

  return (
    <Box>
      <CustomButton variant="text" onClick={handleClick} startIcon={<HelpIcon />}>
        Help
      </CustomButton>
      <Menu
        id="support-menu"
        anchorEl={anchorElement}
        keepMounted
        open={Boolean(anchorElement)}
        onClose={handleClose}
        slotProps={{
          transition: {
            onExited: () => {
              const openSupport = pendingSupportOpen.current;
              pendingSupportOpen.current = null;
              openSupport?.();
            },
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {hasTutorials && (
          <MenuItem
            onClick={() => {
              startTutorial();
              // Track help menu navigation
              navigationMetrics.clicked({
                menu_item: 'help_tutorial',
                source_path: location.pathname,
                destination_path: 'modal', // Tutorial is a modal/overlay
                is_dropdown: true,
              });
              // Keep legacy tracking for now
              trackEvent({
                event: 'help_tutorial_clicked',
                platform: platform,
              });
              handleClose();
            }}
            sx={{ color: 'textColors.lightHeader' }}
          >
            Show Page Tutorial
          </MenuItem>
        )}
        {guidePath ? (
          <MenuItem
            component={Link}
            to={guidePath}
            state={guideEntryState(location)}
            onClick={handleHelpArticlesClick}
            sx={{ color: 'textColors.lightHeader', textDecoration: 'none' }}
          >
            Help Articles
          </MenuItem>
        ) : (
          <MenuItem
            component="a"
            href="https://jotpsych.frontkb.com/en"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleHelpArticlesClick}
            sx={{ color: 'textColors.lightHeader', textDecoration: 'none' }}
          >
            Help Articles
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            // Track help menu navigation
            navigationMetrics.clicked({
              menu_item: 'help_consent_information',
              source_path: location.pathname,
              destination_path: 'modal',
              is_dropdown: true,
            });
            // Keep legacy tracking for now
            trackEvent({
              event: 'help_consent_information_clicked',
              platform: platform,
            });
            // Open consent modal - it's self-contained and needs no props
            open(MODAL_TYPES.CONSENT_INFORMATION, {});
            handleClose();
          }}
          sx={{ color: 'textColors.lightHeader' }}
        >
          Patient Consent & Privacy
        </MenuItem>
        {/* "Feature Updates" (formerly "What's New?") menu item removed - feature has been retired */}
        <MenuItem
          {...(onContactSupport
            ? {}
            : {
                component: 'a' as const,
                href: `https://${brandLanguage.brandSite}/contact`,
                target: '_blank',
                rel: 'noopener noreferrer',
              })}
          onClick={() => {
            // Track help menu navigation
            navigationMetrics.clicked({
              menu_item: 'help_contact',
              source_path: location.pathname,
              destination_path: onContactSupport ? 'modal' : 'external',
              is_dropdown: true,
            });
            // Keep legacy tracking for now
            trackEvent({
              event: 'help_contactus_clicked',
              platform: platform,
            });
            // Wait for the menu's focus trap to release before support captures its opener.
            if (onContactSupport) {
              pendingSupportOpen.current = () => {
                anchorElement?.focus({ preventScroll: true });
                onContactSupport();
              };
            }
            handleClose();
          }}
          sx={{ color: 'textColors.lightHeader', textDecoration: 'none' }}
        >
          Contact Support
        </MenuItem>
        <MenuItem
          onClick={() => {
            // Track help menu navigation
            navigationMetrics.clicked({
              menu_item: 'help_diagnostics',
              source_path: location.pathname,
              destination_path: '/diagnostics',
              is_dropdown: true,
            });
            // Keep legacy tracking for now
            trackEvent({
              event: 'help_diagnostics_clicked',
              platform: platform,
            });
            navigate('/diagnostics');
            handleClose();
          }}
          sx={{ color: 'textColors.lightHeader' }}
        >
          Troubleshooting Tools
        </MenuItem>
      </Menu>
    </Box>
  );
};
